import React, { useEffect, useState } from "react";
import Dialog from "@material-ui/core/Dialog";
import DialogActions from "@material-ui/core/DialogActions";
import DialogContent from "@material-ui/core/DialogContent";
import Button from "@material-ui/core/Button";
import CircularProgress from "@material-ui/core/CircularProgress";
import Typography from "@material-ui/core/Typography";
import { makeStyles, alpha } from "@material-ui/core/styles";
import WarningRoundedIcon from "@material-ui/icons/WarningRounded";
import ErrorRoundedIcon from "@material-ui/icons/ErrorRounded";

import api from "../../services/api";
import toastError from "../../errors/toastError";
import { FormDialogHeader, useFormDialogStyles } from "../../components/FormDialog";

const useStyles = makeStyles((theme) => ({
  list: {
    margin: theme.spacing(1, 0, 0),
    paddingLeft: theme.spacing(2.5),
    lineHeight: 1.6,
    color: theme.palette.text.secondary,
    "& b": { color: theme.palette.text.primary, fontWeight: 600 },
  },
  banner: {
    display: "flex",
    gap: theme.spacing(1),
    alignItems: "flex-start",
    padding: theme.spacing(1.25, 1.5),
    borderRadius: 10,
    fontSize: 13,
    lineHeight: 1.45,
    marginTop: theme.spacing(1.5),
    backgroundColor: alpha(theme.palette.warning.main, theme.palette.type === "dark" ? 0.16 : 0.12),
    "& svg": { color: theme.palette.warning.main, fontSize: 18, marginTop: 1, flexShrink: 0 },
  },
  bannerError: {
    marginTop: 0,
    backgroundColor: alpha(theme.palette.error.main, theme.palette.type === "dark" ? 0.16 : 0.1),
    "& svg": { color: theme.palette.error.main },
  },
  dangerButton: {
    backgroundColor: theme.palette.error.main,
    color: theme.palette.error.contrastText,
    "&:hover": { backgroundColor: theme.palette.error.dark },
  },
  loading: { display: "flex", justifyContent: "center", padding: theme.spacing(3) },
}));

const plural = (n, one, many) => `${n} ${n === 1 ? one : many}`;

// Confirmação de exclusão que diz o que acontece (atendimentos abertos,
// agendamentos futuros, respostas rápidas) e bloqueia a própria conta e o
// último administrador.
const UserDeleteDialog = ({ user, open, onClose, onDeleted }) => {
  const classes = useStyles();
  const dialogClasses = useFormDialogStyles();
  const [impact, setImpact] = useState(null);
  const [loading, setLoading] = useState(false);
  const [deleting, setDeleting] = useState(false);

  useEffect(() => {
    if (!open || !user) return;
    let active = true;
    setImpact(null);
    setLoading(true);
    api
      .get(`/users/${user.id}/delete-impact`)
      .then(({ data }) => active && setImpact(data))
      .catch((err) => {
        if (!active) return;
        toastError(err);
        onClose();
      })
      .finally(() => active && setLoading(false));
    return () => {
      active = false;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, user]);

  const handleDelete = async () => {
    setDeleting(true);
    try {
      await api.delete(`/users/${user.id}`);
      onDeleted(user);
      onClose();
    } catch (err) {
      toastError(err);
    } finally {
      setDeleting(false);
    }
  };

  const blocked = impact && (impact.isSelf || impact.isLastAdmin);
  const firstName = user?.name?.split(" ")[0] || "o usuário";

  return (
    <Dialog
      open={open}
      onClose={deleting ? undefined : onClose}
      maxWidth="xs"
      fullWidth
      classes={{ paper: dialogClasses.paper }}
      aria-labelledby="user-delete-title"
    >
      <FormDialogHeader
        id="user-delete-title"
        title={blocked ? "Não é possível excluir" : `Excluir ${user?.name || ""}?`}
        subtitle={blocked ? user?.name : "Essa ação não pode ser desfeita."}
        onClose={onClose}
        disabled={deleting}
      />
      <DialogContent dividers>
        {loading || !impact ? (
          <div className={classes.loading}>
            <CircularProgress size={28} />
          </div>
        ) : blocked ? (
          <div className={`${classes.banner} ${classes.bannerError}`}>
            <ErrorRoundedIcon />
            <span>
              {impact.isSelf
                ? "Você não pode excluir a sua própria conta. Peça para outro administrador fazer isso."
                : "Este é o último administrador da empresa. Promova outro usuário a Admin antes de excluí-lo."}
            </span>
          </div>
        ) : (
          <>
            <Typography variant="body2">O que acontece ao excluir:</Typography>
            <ul className={classes.list}>
              <li>
                {impact.openTickets > 0 ? (
                  <>
                    <b>{plural(impact.openTickets, "atendimento aberto", "atendimentos abertos")}</b>{" "}
                    {impact.openTickets === 1 ? "volta" : "voltam"} para <b>Aguardando</b>.
                  </>
                ) : (
                  "Nenhum atendimento aberto com ele."
                )}
              </li>
              {impact.futureAppointments > 0 && (
                <li>
                  <b>{plural(impact.futureAppointments, "agendamento futuro", "agendamentos futuros")}</b>{" "}
                  {impact.futureAppointments === 1 ? "fica" : "ficam"} sem profissional.
                </li>
              )}
              {impact.quickMessages > 0 && (
                <li>
                  <b>{plural(impact.quickMessages, "resposta rápida", "respostas rápidas")}</b> criadas por{" "}
                  {firstName} {impact.quickMessages === 1 ? "é apagada" : "são apagadas"}.
                </li>
              )}
            </ul>
            <div className={classes.banner}>
              <WarningRoundedIcon />
              <span>
                Se {firstName} só saiu temporariamente, troque a senha em vez de excluir. Assim o
                histórico de atendimentos continua com o nome.
              </span>
            </div>
          </>
        )}
      </DialogContent>
      <DialogActions className={dialogClasses.actions}>
        {blocked ? (
          <Button
            variant="contained"
            color="primary"
            disableElevation
            className={dialogClasses.actionButton}
            onClick={onClose}
          >
            Entendi
          </Button>
        ) : (
          <>
            <Button
              onClick={onClose}
              disabled={deleting}
              className={`${dialogClasses.actionButton} ${dialogClasses.cancelButton}`}
            >
              Cancelar
            </Button>
            <div className={dialogClasses.progressWrapper}>
              <Button
                variant="contained"
                disableElevation
                disabled={loading || !impact || deleting}
                className={`${dialogClasses.actionButton} ${classes.dangerButton}`}
                onClick={handleDelete}
              >
                Excluir usuário
              </Button>
              {deleting && <CircularProgress size={24} className={dialogClasses.buttonProgress} />}
            </div>
          </>
        )}
      </DialogActions>
    </Dialog>
  );
};

export default UserDeleteDialog;
